/**
 * Display-mode detection (docs/design/pwa.md section 1). The gate is a render decision:
 * nothing here redirects. Order of signals:
 *
 * | Signal                                       | how             | Trusted on |
 * |----------------------------------------------|-----------------|------------|
 * | (display-mode: standalone)                   | dm-standalone   | everywhere |
 * | (display-mode: fullscreen)                   | dm-fullscreen   | everywhere |
 * | (display-mode: minimal-ui)                   | dm-minimal      | everywhere |
 * | (display-mode: window-controls-overlay)      | dm-wco          | desktop    |
 * | navigator.standalone === true                | ios-legacy      | iOS        |
 * | document.referrer starts with android-app:// | twa             | Android    |
 * | ?src=pwa launch flag (start_url)             | launch-flag     | Android only (Samsung Internet workaround) |
 * | none of the above                            | browser         |            |
 *
 * The launch flag is read on the first navigation, kept in sessionStorage and stripped from the
 * URL with history.replaceState. An iPhone Safari bookmark of /?src=pwa never passes the gate.
 */

export type DisplayHow =
  | 'dm-standalone'
  | 'dm-fullscreen'
  | 'dm-minimal'
  | 'dm-wco'
  | 'ios-legacy'
  | 'twa'
  | 'launch-flag'
  | 'browser';

export interface DisplayMode {
  standalone: boolean;
  how: DisplayHow;
}

/** What detectMode reads. Tests pass a fake; the app passes the real window. */
export interface DisplayEnv {
  matchMedia(query: string): { matches: boolean };
  navigator: { userAgent: string; standalone?: boolean };
  referrer: string;
  launchFlag: boolean;
}

const LAUNCH_FLAG_KEY = 'tts.launch';

export function detectMode(env: DisplayEnv): DisplayMode {
  const mq = (q: string) => {
    try {
      return env.matchMedia(`(display-mode: ${q})`).matches;
    } catch {
      return false;
    }
  };
  if (mq('standalone')) return { standalone: true, how: 'dm-standalone' };
  if (mq('fullscreen')) return { standalone: true, how: 'dm-fullscreen' };
  if (mq('minimal-ui')) return { standalone: true, how: 'dm-minimal' };
  if (mq('window-controls-overlay')) return { standalone: true, how: 'dm-wco' };
  if (env.navigator.standalone === true) return { standalone: true, how: 'ios-legacy' };
  if (env.referrer.startsWith('android-app://')) return { standalone: true, how: 'twa' };
  if (/Android/i.test(env.navigator.userAgent) && env.launchFlag) return { standalone: true, how: 'launch-flag' };
  return { standalone: false, how: 'browser' };
}

/** Reads ?src=pwa once, remembers it for this tab, and strips it from the address bar. */
export function readLaunchFlag(): boolean {
  let flag = false;
  try {
    flag = sessionStorage.getItem(LAUNCH_FLAG_KEY) === '1';
  } catch {
    // Storage can be blocked. The URL check below still works for this load.
  }
  const url = new URL(window.location.href);
  if (url.searchParams.get('src') === 'pwa') {
    flag = true;
    try {
      sessionStorage.setItem(LAUNCH_FLAG_KEY, '1');
    } catch {
      // ignore
    }
    url.searchParams.delete('src');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }
  return flag;
}

let cached: DisplayMode | null = null;
let launchFlag: boolean | null = null;

function browserEnv(): DisplayEnv {
  if (launchFlag === null) launchFlag = readLaunchFlag();
  return {
    matchMedia: (q) => window.matchMedia(q),
    navigator: window.navigator as Navigator & { standalone?: boolean },
    referrer: document.referrer,
    launchFlag,
  };
}

/** The current mode, cached. Call `refreshDisplayMode()` when a media query flips. */
export function displayMode(): DisplayMode {
  if (!cached) cached = detectMode(browserEnv());
  return cached;
}

export function refreshDisplayMode(): DisplayMode {
  cached = detectMode(browserEnv());
  return cached;
}

/** Value for the X-TTS-Display header. */
export function displayHeader(): 'standalone' | 'browser' {
  return displayMode().standalone ? 'standalone' : 'browser';
}

/** Calls back when the display mode may have changed (install from a desktop tab, for example). */
export function onDisplayModeChange(cb: (m: DisplayMode) => void): () => void {
  const queries = ['standalone', 'fullscreen', 'minimal-ui'].map((q) => window.matchMedia(`(display-mode: ${q})`));
  const handler = () => cb(refreshDisplayMode());
  for (const q of queries) q.addEventListener?.('change', handler);
  return () => {
    for (const q of queries) q.removeEventListener?.('change', handler);
  };
}

/** Raw display signals. The gate's sheet uses lib/diagnostics.ts, which is safe to paste into a chat. */
export function displayDiagnostics(): Record<string, string> {
  const mq = (q: string) => String(window.matchMedia(`(display-mode: ${q})`).matches);
  return {
    how: displayMode().how,
    standalone: mq('standalone'),
    fullscreen: mq('fullscreen'),
    'minimal-ui': mq('minimal-ui'),
    browser: mq('browser'),
    'navigator.standalone': String((navigator as Navigator & { standalone?: boolean }).standalone ?? 'n/a'),
    referrer: referrerOrigin(),
    userAgent: navigator.userAgent.slice(0, 160),
  };
}

function referrerOrigin(): string {
  try {
    return document.referrer ? new URL(document.referrer).origin : '(none)';
  } catch {
    return '(none)';
  }
}
